/**
 * contactChange.js — TEST 3.01: change the account e-mail or mobile number from Settings.
 *
 * Both changes are proven on the NEW value with a code (API lane/a301):
 *   e-mail: POST /auth/contact/email/code -> {changeToken} -> POST /auth/contact/email/verify -> {email, token}
 *   mobile: POST /auth/phone/send-code (re-auth when a number is REPLACED) -> {changeToken}
 *           -> POST /auth/phone/verify with that changeToken -> {phone, token}
 * The server bumps token_version on the swap, so the old session dies at once: the fresh `token` in the answer
 * must replace the stored one (services/api.js does it inside the wrapper, never the screen).
 *
 * ★ Pure: no imports, no i18n, no fetching, so scripts/check-contact-change.mjs can run it as it is.
 */

export const CONTACT_EMAIL_ROUTE = '/settings/email'
export const CONTACT_PHONE_ROUTE = '/settings/phone'

/**
 * Every refusal `code` the two contact flows can receive (API B301a section 2 + B301b section 2), mapped to the
 * i18n key of its plain sentence. Codes handled elsewhere are listed in HANDLED_ELSEWHERE below.
 */
export const CONTACT_REFUSAL_KEYS = Object.freeze({
  // e-mail, send
  email_invalid: 'auth.invalidEmail',
  email_unchanged: 'contact.emailUnchanged', // R1
  email_private_relay: 'contact.emailPrivateRelay', // R3
  email_code_rate: 'contact.tooManyTries', // R6
  email_send_failed: 'contact.emailSendFailed', // R7
  // e-mail, verify (R301B: an address held by another account is refused only here, after the code; /code mails it
  //   like a free one, so the send answer says nothing about who holds an address)
  email_in_use: 'contact.emailInUse', // R2
  email_code_invalid: 'onboarding.phone.badCode', // R4 = reuse (Alex)
  email_code_expired: 'contact.codeExpired', // R5
  email_code_attempts: 'contact.tooManyTries', // R6
  email_changed_meanwhile: 'contact.emailChangedMeanwhile', // R8
  email_write_failed: 'errors2.somethingWentWrong',
  // mobile, send
  phone_invalid: 'onboarding.phone.sendFailed',
  turnstile_failed: 'auth.verifyHuman',
  phone_unchanged: 'contact.phoneUnchanged', // R10
  phone_code_rate: 'contact.phoneCodeRate', // R11
  phone_send_failed: 'onboarding.phone.sendFailed',
  // mobile, verify (R301B: a number held by another account is refused only here, after the code; send-code texts it
  //   like a free one. The code is then spent, so the screen goes back to the number step)
  phone_in_use: 'contact.phoneInUse', // R9
  phone_code_missing: 'onboarding.phone.badCode',
  sms_opt_in_invalid: 'errors2.somethingWentWrong',
  phone_change_expired: 'contact.codeExpired', // R5 (reuse)
  phone_code_attempts: 'contact.tooManyTries', // R6 (reuse)
  phone_code_invalid: 'onboarding.phone.badCode', // R4 (reuse)
  phone_write_failed: 'errors2.somethingWentWrong',
  // the shared re-authentication
  reauth_required: 'settingsMisc.reauthNeeded',
  wrong_password: 'settingsMisc.incorrectPassword',
  reauth_mismatch: 'settingsMisc.reauthMismatch',
  reauth_failed: 'settingsMisc.reauthMismatch',
  // both
  account_unavailable: 'errors2.somethingWentWrong',
  forbidden: 'errors2.somethingWentWrong',
})

/**
 * Codes that are answered, but not by a sentence from the table above:
 *   account_locked  -> the lock countdown (LockNotice, `retryAfter` seconds)
 *   sms_us_ca_only  -> the server's own sentence (utils/smsRefusal, PROD-807)
 *   2fa_current_factor_* -> PROD-1154's field (utils/currentFactor)
 */
export const HANDLED_ELSEWHERE = Object.freeze(['account_locked', 'sms_us_ca_only',
  '2fa_current_factor_required', '2fa_current_factor_invalid', '2fa_current_factor_unconfirmed'])

/** The machine code of a refusal: `code` first, then the older `errorType` the re-auth also sends. */
export function refusalCode(err) {
  if (!err || typeof err !== 'object') return null
  if (typeof err.code === 'string' && err.code) return err.code
  if (typeof err.errorType === 'string' && err.errorType) return err.errorType
  return null
}

/**
 * What the screen does with a refusal:
 *   { kind: 'locked', seconds }       the re-auth lock (account_locked)
 *   { kind: 'reauth', code, providers } the re-auth was refused or is missing (the method may switch)
 *   { kind: 'restart', key }          the pending change is gone: back to the first step
 *   { kind: 'key', key }              a plain sentence
 *   null                              not a contact refusal (network, 5xx without a code, 1154, PROD-807)
 */
const RESTART = new Set(['email_changed_meanwhile', 'email_in_use', 'email_unchanged', 'phone_in_use', 'phone_unchanged'])
const REAUTH = new Set(['reauth_required', 'wrong_password', 'reauth_mismatch', 'reauth_failed'])
export function contactRefusal(err, { atVerify = false } = {}) {
  const code = refusalCode(err)
  if (!code) return null
  if (code === 'account_locked') {
    const s = Number(err.retryAfter ?? err.retry_after ?? err.remainingSeconds)
    return { kind: 'locked', seconds: s > 0 ? Math.ceil(s) : 900 }
  }
  const key = CONTACT_REFUSAL_KEYS[code]
  if (!key) return null
  if (REAUTH.has(code)) return { kind: 'reauth', code, key, providers: Array.isArray(err.providers) ? err.providers : null }
  if (atVerify && RESTART.has(code)) return { kind: 'restart', code, key }
  return { kind: 'key', code, key }
}

/**
 * The re-authentication fields for a send, from what the patient gave (the Delete Account fields):
 *   password -> { currentPassword }, an Apple / Google sign-in -> { reauthProvider, reauthToken }.
 * null = nothing to prove with: the screen sends nothing.
 */
export function reauthFields(method, { password = '', token = '' } = {}) {
  if (method === 'password') return password ? { currentPassword: password } : null
  if (method === 'google' || method === 'apple') return token ? { reauthProvider: method, reauthToken: token } : null
  return null
}

const digits = (v) => String(v ?? '').replace(/\D/g, '')

/** A ten-digit US / Canada number in its display shape, "(555) 123-4567"; anything else as it is. */
export function displayPhone(v) {
  const d = digits(v).slice(-10)
  if (d.length !== 10) return String(v ?? '')
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** The Settings row subtitle: the account number with only its last four digits shown. '' when there is none. */
export function maskedPhone(v) {
  const d = digits(v)
  if (d.length < 4) return ''
  return `(•••) •••-${d.slice(-4)}`
}

/** Same number in any format (the server's own R10 test compares the last ten digits). */
export const samePhone = (a, b) => {
  const x = digits(a).slice(-10)
  return x.length === 10 && x === digits(b).slice(-10)
}

/** A plausible new address: one @, a dot in the domain, no spaces, at most 254 characters (the server decides). */
export const emailShaped = (v) => {
  const s = String(v ?? '').trim()
  return s.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
}

/** The resend countdown after a code was sent, in seconds. */
export const RESEND_SECONDS = 60
